import React, { useState, useEffect } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../Attendance/Attendance.css";

function Attendance() {
  const [attendanceList, setAttendanceList] = useState([]);
  const [filteredAttendance, setFilteredAttendance] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [attendanceFilter, setAttendanceFilter] = useState("");
  const [classFilter, setClassFilter] = useState("");
  const [sectionFilter, setSectionFilter] = useState("");
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [loading, setLoading] = useState(true);
  const [classList, setClassList] = useState([]);
  const [sectionList, setSectionList] = useState([]);

  const today = new Date();
  const todayString = today.toISOString().split("T")[0];
  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(today.getDate() - 7);
  const sevenDaysAgoString = sevenDaysAgo.toISOString().split("T")[0];

  const handleDateChange = (e) => {
    const selected = e.target.value;
    setSelectedDate(selected);
    fetchAttendanceData(selected);
  };

  const fetchAttendanceData = async (date) => {
    try {
      const response = await axios.get(`https://aadyaacademy.org/api/attendance?date=${date}`);
      if (response.data.attendance && response.data.attendance.length > 0) {
        const sortedAttendance = response.data.attendance.sort((a, b) =>
          a.student_name.localeCompare(b.student_name)
        );
        setAttendanceList(sortedAttendance);
        setFilteredAttendance(sortedAttendance);
      } else {
        const defaultAttendance = attendanceList.map((student) => ({
          ...student,
          attendance_status: "Not Given",
          attendance_date: date,
        }));
        const sortedDefault = defaultAttendance.sort((a, b) =>
          a.student_name.localeCompare(b.student_name)
        );
        setFilteredAttendance(sortedDefault);
      }
    } catch (error) {
      console.error("Error fetching data:", error.response?.data || error.message);
      toast.error("Failed to load data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttendanceData(todayString);
    const fetchInitialData = async () => {
      try {
        const [classResponse, sectionResponse] = await Promise.all([
          axios.get("https://aadyaacademy.org/api/classes"),
          axios.get("https://aadyaacademy.org/api/sections"),
        ]);
        if (Array.isArray(classResponse.data.classes)) {
          setClassList(classResponse.data.classes.map((cls) => cls.name));
        }
        if (Array.isArray(sectionResponse.data.sections)) {
          setSectionList(sectionResponse.data.sections.map((sec) => sec.name));
        }
      } catch (error) {
        console.error("Error fetching initial data:", error);
        toast.error("Failed to load initial data.");
      }
    };
    fetchInitialData();
  }, []);

  const handleAttendanceChange = (index, newAttendance) => {
    const updatedFilteredAttendance = [...filteredAttendance];
    updatedFilteredAttendance[index].attendance_status = newAttendance;
    setFilteredAttendance(updatedFilteredAttendance);
  };

  const checkAttendanceExists = async (date, studentIds) => {
    try {
      const response = await axios.get(`https://aadyaacademy.org/api/check-attendance`, {
        params: {
          date,
          student_ids: studentIds.join(","),
        },
      });
      return response.data.exists;
    } catch (error) {
      console.error("Error checking attendance:", error.response?.data || error.message);
      toast.error("Failed to check attendance. Please try again.");
      return false;
    }
  };

  const saveAttendance = async (attendanceData) => {
    try {
      await axios.post("https://aadyaacademy.org/api/attendance", {
        date: selectedDate,
        attendance_data: attendanceData,
      });
      toast.success("Attendance saved successfully!", {
        autoClose: 3000,
      });
      fetchAttendanceData(selectedDate);
    } catch (error) {
      console.error("Error saving attendance:", error.response?.data || error.message);
      toast.error("Failed to save attendance. Please try again.");
    }
  };

  const handleSaveAttendance = async () => {
    if (!selectedDate) {
      toast.error("Please select a date to save attendance.");
      return;
    }

    const attendanceData = filteredAttendance.map((student) => ({
      student_id: student.student_id,
      attendance_status: student.attendance_status,
      attendance_date: selectedDate,
    }));

    const studentIds = attendanceData.map((item) => item.student_id);
    const attendanceExists = await checkAttendanceExists(selectedDate, studentIds);

    if (attendanceExists) {
      const confirmUpdate = window.confirm(
        "Attendance already exists for some students on this date. Do you want to update it?"
      );
      if (confirmUpdate) {
        await saveAttendance(attendanceData);
      } else {
        toast.info("Attendance save cancelled.", {
          autoClose: 3000,
        });
      }
    } else {
      await saveAttendance(attendanceData);
    }
  };

  const formatDate = (date) => {
    const newDate = new Date(date);
    return `${newDate.getDate()}/${newDate.getMonth() + 1}/${newDate.getFullYear()}`;
  };

  const filterAttendance = () => {
    let filteredData = attendanceList;
    if (searchTerm) {
      filteredData = filteredData.filter(
        (student) =>
          student.student_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          student.student_id.toString().includes(searchTerm)
      );
    }
    if (classFilter) {
      filteredData = filteredData.filter((student) => student.class === classFilter);
    }
    if (sectionFilter) {
      filteredData = filteredData.filter((student) => student.section === sectionFilter);
    }
    if (attendanceFilter) {
      filteredData = filteredData.filter((student) => student.attendance_status === attendanceFilter);
    }
    filteredData = filteredData.sort((a, b) => a.student_name.localeCompare(b.student_name));
    setFilteredAttendance(filteredData);
  };

  useEffect(() => {
    filterAttendance();
  }, [searchTerm, classFilter, sectionFilter, attendanceFilter, attendanceList]);

  return (
    <div className="attendance">
      <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} closeOnClick />
      <div className="attendance-table">
        <div className="table-header">
          <div className="table-header-top">
            <h2>Attendance</h2>
          </div>
          <div className="table-header-btm">
            <input
              type="text"
              className="search-input"
              placeholder="Search by Name or ID..."
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <input
              type="date"
              className="date-picker"
              value={selectedDate}
              max={todayString}
              min={sevenDaysAgoString}
              onChange={handleDateChange}
            />
            <select className="class-filter" onChange={(e) => setClassFilter(e.target.value)}>
              <option value="">Class</option>
              {classList.map((classItem, id) => (
                <option key={id} value={classItem}>
                  {classItem}
                </option>
              ))}
            </select>
            <select className="section-filter" onChange={(e) => setSectionFilter(e.target.value)}>
              <option value="">Section</option>
              {sectionList.map((section, id) => (
                <option key={id} value={section}>
                  {section}
                </option>
              ))}
            </select>
            <select
              className="attendance-filter"
              onChange={(e) => setAttendanceFilter(e.target.value)}
            >
              <option value="">Attendance Status</option>
              <option value="Present">Present</option>
              <option value="Absent">Absent</option>
              <option value="Half Day">Half Day</option>
              <option value="Not Given">Not Given</option>
            </select>
          </div>
        </div>

        <table className="table-content">
          <thead>
            <tr>
              <th className="thead-content">Id</th>
              <th className="thead-content">Name</th>
              <th className="thead-content">Class</th>
              <th className="thead-content">Section</th>
              <th className="thead-content">Date</th>
              <th className="thead-content">Attendance</th>
            </tr>
          </thead>
          <tbody>
            {filteredAttendance.length === 0 ? (
              <tr>
                <td colSpan="6">No attendance found for selected date. Please mark attendance below:</td>
              </tr>
            ) : (
              filteredAttendance.map((student, index) => (
                <tr key={student.student_id}>
                  <td className="tbody-content">{student.student_id}</td>
                  <td className="tbody-content">{student.student_name}</td>
                  <td className="tbody-content">{student.class}</td>
                  <td className="tbody-content">{student.section}</td>
                  <td className="tbody-content">{formatDate(student.attendance_date)}</td>
                  <td>
                    <span
                      onClick={() => handleAttendanceChange(index, "Present")}
                      className={`attendance-option ${
                        student.attendance_status === "Present" ? "selected" : ""
                      }`}
                      style={{
                        fontSize: "12px",
                        padding: "5px 10px",
                        margin: "5px",
                        borderRadius: "5px",
                        backgroundColor:
                          student.attendance_status === "Present" ? "#28a745" : "transparent",
                        color: "black",
                      }}
                    >
                      Present
                    </span>
                    <span
                      onClick={() => handleAttendanceChange(index, "Absent")}
                      className={`attendance-option ${
                        student.attendance_status === "Absent" ? "selected" : ""
                      }`}
                      style={{
                        fontSize: "12px",
                        padding: "5px 10px",
                        margin: "5px",
                        borderRadius: "5px",
                        backgroundColor:
                          student.attendance_status === "Absent" ? "#dc3545" : "transparent",
                        color: "black",
                      }}
                    >
                      Absent
                    </span>
                    <span
                      onClick={() => handleAttendanceChange(index, "Half Day")}
                      className={`attendance-option ${
                        student.attendance_status === "Half Day" ? "selected" : ""
                      }`}
                      style={{
                        fontSize: "12px",
                        padding: "5px 10px",
                        margin: "5px",
                        borderRadius: "5px",
                        backgroundColor:
                          student.attendance_status === "Half Day" ? "#ffc107" : "transparent",
                        color: "black",
                      }}
                    >
                      Half Day
                    </span>
                    <span
                      onClick={() => handleAttendanceChange(index, "Not Given")}
                      className={`attendance-option ${
                        student.attendance_status === "Not Given" ? "selected" : ""
                      }`}
                      style={{
                        fontSize: "12px",
                        padding: "5px 10px",
                        margin: "5px",
                        borderRadius: "5px",
                        backgroundColor:
                          student.attendance_status === "Not Given" ? "#6c757d" : "transparent",
                        color: "black",
                      }}
                    >
                      Not Given
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <div className="save-attendance">
          <button
            className="save-button"
            onClick={handleSaveAttendance}
            style={{
              backgroundColor: "#007bff",
              color: "white",
              padding: "10px 20px",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer",
            }}
            onMouseOver={(e) => (e.target.style.backgroundColor = "#0056b3")}
            onMouseOut={(e) => (e.target.style.backgroundColor = "#007bff")}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

export default Attendance;